import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UnavailableState } from '../../../shared/dashboard/UnavailableState';
import { formatCurrency, formatPercent, type TrendInfo } from '../types';

/** One of the top summary cards. `trend === null` renders NO badge at all — the direct fix for
 * legacy's confirmed "divide by a fake 1 when the prior period is zero" bug, which produced a huge,
 * meaningless green badge. See docs/dashboard-architecture.md.
 *
 * `valueText === null` means the figure could not be computed. The card then says "Data unavailable" (with the
 * reason, when given) instead of a zero, and is never a link: there is nothing to drill into.
 *
 * `destinationHref` is optional and defaults to `undefined` — omitting it renders exactly the plain,
 * non-interactive card every existing caller (the standalone `/dashboard`) already gets. Only a caller
 * that explicitly passes a real route (EPN-42's Admin Dashboard) gets a clickable/keyboard-operable card,
 * per the "must have a valid destination, never a decorative affordance" rule — no destination, no link
 * semantics, ever. The link wraps only the value and is stretched over the card, so the explanation button
 * (`extraHeaderContent`) is never nested inside a link. */
export function StatCard({
  label, valueText, trend, icon, extraHeaderContent, destinationHref, comparisonText = 'vs last month', unavailableReason, unavailableTitle, trendNeutral = false,
}: {
  label: string;
  valueText: string | null;
  trend: TrendInfo | null;
  icon: ReactNode;
  extraHeaderContent?: ReactNode | undefined;
  destinationHref?: string | undefined;
  comparisonText?: string | undefined;
  unavailableReason?: string | undefined;
  unavailableTitle?: string | undefined;
  /** For a figure where higher is not good and lower is not bad (a cost): the badge keeps the arrow and the word, without the green/red verdict. */
  trendNeutral?: boolean | undefined;
}) {
  const header = (
    <div className="ed-stat-header">
      <span className="ed-stat-label">
        {label}
        {extraHeaderContent}
      </span>
      <span className="ed-icon-circle" aria-hidden="true">{icon}</span>
    </div>
  );

  if (valueText === null) {
    return (
      <div className="ed-card ed-stat-card" data-testid="stat-card-unavailable">
        {header}
        <UnavailableState title={unavailableTitle} reason={unavailableReason} />
        <div className="ed-stat-footer" />
      </div>
    );
  }

  const footer = (
    <div className="ed-stat-footer">
      {trend && (
        <span className={`ed-trend ${trendNeutral ? 'ed-trend--neutral' : trend.isUp ? 'ed-trend--up' : 'ed-trend--down'}`} role="status">
          <span aria-hidden="true">{trend.isUp ? '↑' : '↓'}</span>
          {formatPercent(trend.percentChange)}
          <span className="ed-visually-hidden">{trend.isUp ? ' higher' : ' lower'}</span>
        </span>
      )}
      <span className="ed-trend-text">{comparisonText}</span>
    </div>
  );

  if (destinationHref) {
    return (
      <div className="ed-card ed-stat-card ed-stat-card--clickable">
        {header}
        <Link to={destinationHref} className="ed-stat-link" aria-label={`${label}: ${valueText}. View details.`}>
          <div className="ed-stat-value">{valueText}</div>
        </Link>
        {footer}
      </div>
    );
  }

  return (
    <div className="ed-card ed-stat-card">
      {header}
      <div className="ed-stat-value">{valueText}</div>
      {footer}
    </div>
  );
}

interface CommonCardProps {
  label: string;
  trend: TrendInfo | null;
  icon: ReactNode;
  extraHeaderContent?: ReactNode | undefined;
  destinationHref?: string | undefined;
  comparisonText?: string | undefined;
  unavailableReason?: string | undefined;
  unavailableTitle?: string | undefined;
  trendNeutral?: boolean | undefined;
}

export function MoneyStatCardBody(props: CommonCardProps & { amount: number | null }) {
  const { amount, ...rest } = props;
  return <StatCard {...rest} valueText={amount === null ? null : formatCurrency(amount)} />;
}

export function CountStatCardBody(props: CommonCardProps & { count: number | null }) {
  const { count, ...rest } = props;
  return <StatCard {...rest} valueText={count === null ? null : String(count)} />;
}
